import React, { useCallback, useEffect, useState } from "react";
import {
  usePlaidLink,
  PlaidLinkOnSuccessMetadata,
  PlaidLinkError,
  PlaidLinkOnExitMetadata,
} from "react-plaid-link";
import { useUser } from "../../contexts/UserContext";
import { Button } from "flowbite-react";
import LoadingState from "./LoadingState";
import ErrorState from "./ErrorState";
import { getApiBaseUrl } from "../../config/api";
import { BankLinkingService } from "../../domain/services/banks/BankLinkService";
import { FirestoreUserBankRepository } from "../../infrastructure/repositories/UserBankRepository";
import { FirestoreBankAccountRepository } from "../../infrastructure/repositories/BankAccountRepository";
import { FirestoreAppUserRepository } from "../../infrastructure/repositories/appUserRepository";

interface PlaidLinkButtonProps {
  onAccountLinked?: () => void;
  children?: React.ReactNode;
  className?: string;
}

const PlaidLinkButton: React.FC<PlaidLinkButtonProps> = ({
  onAccountLinked,
  children,
  className,
}) => {
  const API_BASE_URL = getApiBaseUrl();
  const [linkToken, setLinkToken] = useState<string | null>(null);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const { currentUser, appUser, loading: userLoading } = useUser();

  useEffect(() => {
    if (!userLoading && currentUser) {
      const fetchLinkToken = async () => {
        try {
          const response = await fetch(`${API_BASE_URL}/api/v1/bank/connect`, {
            headers: {
              Authorization: `Bearer ${await currentUser.getIdToken()}`,
            },
          });

          if (!response.ok) {
            const errorText = await response.text();
            setError(
              Error(
                `Failed to fetch link token. Server responded with: ${errorText}`,
              ),
            );
          }

          const linkToken = await response.text();
          setLinkToken(linkToken);
        } catch (err) {
          console.error("Error fetching Link token:", err);
          setError(err as Error);
        }
      };
      fetchLinkToken();
    }
  }, [currentUser, userLoading, API_BASE_URL]);

  const onSuccess = useCallback(
    async (public_token: string, metadata: PlaidLinkOnSuccessMetadata) => {
      const bankLinkingService = new BankLinkingService(
        new FirestoreAppUserRepository(),
        new FirestoreBankAccountRepository(),
        new FirestoreUserBankRepository(),
      );

      if (!appUser || !currentUser) {
        const err = new Error("User not authenticated in onSuccess callback.");
        console.error(err);
        setError(err);
        return;
      }

      try {
        const response = await fetch(
          `${API_BASE_URL}/api/v1/bank/set_access_token`,
          {
            method: "POST",
            headers: {
              Authorization: `Bearer ${await currentUser.getIdToken()}`,
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              public_token: public_token,
              metadata: metadata,
            }),
          },
        );

        if (!response.ok) {
          const errorText = await response.text();
          setError(
            Error(
              `Failed to exchange public token. Server responded with: ${errorText}`,
            ),
          );
        }

        await bankLinkingService.handlePlaidSuccess(
          appUser.appUserId,
          metadata,
        );

        if (onAccountLinked) {
          onAccountLinked();
        }
        setShowConfirmation(true);
      } catch (err) {
        console.error("Error in onSuccess:", err);
        setError(err as Error);
      }
    },
    [appUser, currentUser, onAccountLinked, API_BASE_URL],
  );

  const onExit = useCallback(
    (plaidError: PlaidLinkError | null, metadata: PlaidLinkOnExitMetadata) => {
      if (plaidError) {
        console.error("Plaid Link exited with error:", plaidError, metadata);
        setError(
          new Error(
            `Plaid Link exited with error: ${plaidError.error_message}`,
          ),
        );
      } else {
        console.debug("Plaid Link was exited by the user:", metadata);
      }
    },
    [],
  );

  const onEvent = useCallback((eventName: string, metadata: any) => {
    console.debug("Plaid Link event:", eventName, metadata);
  }, []);

  const config: Parameters<typeof usePlaidLink>[0] = {
    token: linkToken ?? "",
    onSuccess,
    onExit,
    onEvent,
  };

  const { open, ready, error: plaidLinkError } = usePlaidLink(config);

  if (userLoading || !currentUser) {
    return <LoadingState message="Loading..." />;
  }

  if (error) {
    return <ErrorState error={error} />;
  }

  if (plaidLinkError) {
    return <ErrorState error={plaidLinkError.message} />;
  }

  if (!linkToken) {
    return <LoadingState message="Loading..." />;
  }

  return (
    <>
      <Button
        onClick={() => {
          if (ready) {
            open();
            setShowConfirmation(false);
          } else {
            const err = new Error("Plaid Link is not ready.");
            console.error(err);
            setError(err);
          }
        }}
        disabled={!ready}
        className={
          className ||
          "py-2 px-5 inline-block font-semibold tracking-wide align-middle duration-500 text-base text-center bg-indigo-600 hover:bg-indigo-700 border border-indigo-600 hover:border-indigo-700 text-white rounded-md w-full"
        }
      >
        {children || "Connect a Bank Account"}
      </Button>

      {showConfirmation && (
        <div
          className="mt-4 bg-green-100 border-l-4 border-green-500 text-green-700 p-4"
          role="alert"
        >
          <p className="font-bold">Account Linked Successfully!</p>
          <p>Your bank account has been successfully linked.</p>
        </div>
      )}
    </>
  );
};

export default PlaidLinkButton;
